<template>
  <!-- Datadog would otherwise name clicks after their text, and the file picker shows the file name -->
  <ResponsiveLayout
    sticky-actions
    data-dd-action-name="Action link upload page"
  >
    <template #header_end><HeaderHelp /></template>

    <div
      class="kds-main-wrapper"
      data-qa="upload-prescription-link"
    >
      <div
        v-if="state === STATES.CHECKING"
        class="upload-prescription-link__spinner"
        data-qa="checking"
      >
        <Base64Spinner />
      </div>
      <div
        v-else-if="state === STATES.INVALID"
        class="kds-content-block"
        data-qa="invalid"
      >
        <p class="kds-typography-title-xlarge">
          {{ $t('eu__upload_prescription_link__invalid__header') }}
        </p>
        <!-- Catalogue copy with the support links (email, WhatsApp, phone) -->
        <p
          class="kds-typography-paragraph-large"
          v-html="$t('eu__upload_prescription_link__invalid__description')"
        />
      </div>
      <div
        v-else-if="state === STATES.UNAVAILABLE"
        class="kds-content-block"
        data-qa="unavailable"
      >
        <p class="kds-typography-title-xlarge">
          {{ $t('eu__upload_prescription_link__unavailable__header') }}
        </p>
        <p class="kds-typography-paragraph-large">
          {{ $t('eu__upload_prescription_link__unavailable__description') }}
        </p>
      </div>
      <div
        v-else-if="state === STATES.DONE"
        class="kds-content-block"
        data-qa="done"
      >
        <p class="kds-typography-title-xlarge">
          {{ $t('eu__upload_prescription_link__done__header') }}
        </p>
        <p class="kds-typography-paragraph-large">
          {{ $t('eu__upload_prescription_link__done__description') }}
        </p>
      </div>
      <template v-else>
        <div class="kds-content-block">
          <p class="kds-typography-title-xlarge">
            {{ $t('eu__prescription_upload__header') }}
          </p>
          <p class="kds-typography-paragraph-large">
            {{ $t('eu__upload_prescription_link__description') }}
          </p>
        </div>
        <FileSelection
          ref="fileSelectionRef"
          :selection-choose-ctrl-text="
            $t('eu__prescription_upload__file_selection')
          "
          :selection-preview="true"
          data-qa="file-selection"
          selection-hint="(PNG, JPG, PDF | max. 10MB)"
          @reset="handleFileReset"
          @select="handleFileSelection"
        />
        <BaseBanner
          v-if="uploadError"
          scroll-into-view
          :title="$t('eu__prescription_upload__error__header')"
          variant="warning"
          data-qa="upload-error"
        >
          <p>{{ $t('eu__prescription_upload__error__description') }}</p>
        </BaseBanner>
      </template>
    </div>
    <PrescriptionConsentSheet
      ref="confirmationRef"
      @confirm="handleConfirmationSubmit"
      @cancel="handleConfirmationCancel"
    />
    <template
      v-if="state === STATES.READY"
      #actions
    >
      <BaseButton
        :disabled="selectedFile == null || uploading"
        :loading="uploading"
        :text="$t('eu__prescription_upload__primary_cta')"
        data-qa="submit"
        data-dd-action-name="Send prescription"
        @click="handleSubmit"
      />
    </template>
    <template
      v-else-if="state === STATES.UNAVAILABLE"
      #actions
    >
      <BaseButton
        :text="$t('eu__upload_prescription_link__unavailable__retry_cta')"
        data-qa="retry"
        data-dd-action-name="Check action link again"
        @click="checkLink"
      />
    </template>
  </ResponsiveLayout>
</template>

<script setup>
import { onMounted, ref } from 'vue'
import { UPLOAD_PRESCRIPTION_PAGE } from '@shared/actionLinks/pages.js'
import ResponsiveLayout from '@shared/components/ResponsiveLayout.vue'
import Base64Spinner from '@shared/components/Base64Spinner.vue'
import BaseBanner from '@shared/components/BaseBanner.vue'
import BaseButton from '@shared/components/BaseButton.vue'
import FileSelection from '@eu/app/components/FileSelection.vue'
import PrescriptionConsentSheet from '@eu/app/components/PrescriptionConsentSheet.vue'
import HeaderHelp from '@eu/app/components/HeaderHelp.vue'
import { takeAlias } from '@eu/app/actionLinks/captureAlias.js'
import { ERRORS, upload, validate } from '@eu/app/actionLinks/api.js'

const STATES = {
  CHECKING: 'checking',
  READY: 'ready',
  INVALID: 'invalid',
  UNAVAILABLE: 'unavailable',
  DONE: 'done',
}

// The alias is the link's credential and stays while the page is open, for "try again" and the
// upload, until the page ends on invalid or done. It never goes into a store, storage or the URL.
let alias = takeAlias()

const state = ref(STATES.CHECKING)
const selectedFile = ref(null)
const uploading = ref(false)
const uploadError = ref(false)
const confirmationRef = ref(null)
const fileSelectionRef = ref(null)

const checkLink = async () => {
  if (alias == null) {
    state.value = STATES.INVALID
    return
  }

  state.value = STATES.CHECKING
  try {
    await validate(alias, UPLOAD_PRESCRIPTION_PAGE.purpose)
    state.value = STATES.READY
  } catch (error) {
    if (error.kind === ERRORS.INVALID) {
      alias = null
      state.value = STATES.INVALID
    } else {
      state.value = STATES.UNAVAILABLE
    }
  }
}

const handleFileSelection = (files) => {
  selectedFile.value = files[0]
  uploadError.value = false
}

const handleFileReset = () => {
  selectedFile.value = null
  uploadError.value = false
}

const handleSubmit = () => {
  confirmationRef.value.open()
}

const handleConfirmationCancel = () => {
  confirmationRef.value.close()
}

const handleConfirmationSubmit = async () => {
  if (uploading.value || selectedFile.value == null) return

  confirmationRef.value.close()
  uploading.value = true
  uploadError.value = false
  fileSelectionRef.value.nextCtrlState({ state: 'DISABLED' })
  try {
    await upload(alias, selectedFile.value)
    alias = null
    state.value = STATES.DONE
  } catch (error) {
    if (error.kind === ERRORS.INVALID) {
      alias = null
      state.value = STATES.INVALID
    } else {
      // As in the app, every other failure shows the same error
      uploadError.value = true
      fileSelectionRef.value.nextCtrlState({ state: 'DEFAULT' })
    }
  } finally {
    uploading.value = false
  }
}

onMounted(checkLink)
</script>

<style lang="scss" scoped>
.upload-prescription-link__spinner {
  width: 54px;
  height: 54px;
  margin: 32px auto;
}
</style>
